import { useEffect, useRef, useState } from 'react';

/**
 * Fires once an element scrolls into view, for a one-shot fade/rise-in.
 * Cheap (unobserves after the first hit) and purely additive - elements are
 * visible by default in CSS, JS only adds the "not yet revealed" state, so a
 * failed observer (old browser, SSR) never hides content.
 */
export function useReveal<T extends HTMLElement>(options?: IntersectionObserverInit) {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px', ...options },
    );
    io.observe(el);
    return () => io.disconnect();
    // `options` is intentionally excluded: callers pass an inline object, and
    // re-subscribing on every render would defeat the one-shot observer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ref, visible };
}
